import { ID3 } from 'library/ts/enums/metadata/ID3';
import { IImporterReject } from 'library/ts/interfaces/importer/IImporterReject';
import { IPromiseResolveGeneric } from 'library/ts/interfaces/IPromiseResolveGeneric';
import { IResourceConfig } from 'library/ts/player/adapter/interfaces/resource/config/IResourceConfig';
import { IPlayerAdapterAPI } from 'library/ts/player/adapter/IPlayerAdapterAPI';
import { ILogger } from 'library/ts/utilities/logger/interfaces/ILogger';
import { LogService } from 'library/ts/utilities/logger/LogService';
import { Utilities } from 'library/ts/utilities/Utilities';
import { TagEvent } from 'tag/ts/tag/events/TagEvent';
import { ITag } from 'tag/ts/tag/interfaces/ITag';
import { TagErrorCode } from 'tag/ts/tag/model/error/TagErrorCode';
import { IResourceManager } from 'tag/ts/tag/player/managers/player/interfaces/IResourceManager';

/**
 * @copyright © Paramount Streaming
 * @author  Stan Gletski
 * @description Manages resource configuration and loading operations
 *
 * **Resource Management Scope:**
 * - Resource configuration storage and retrieval
 * - Complex resource loading pipeline with dependency management
 * - Owner ID processing for ID3 metadata events
 * - Resource validation and error handling
 * - Integration with player adapter for resource operations
 * - Loading animation coordination (desktop)
 *
 * **Resource Loading Pipeline:**
 * Implements a sophisticated resource loading process:
 * 1. Resource configuration validation
 * 2. Loading animation display (desktop)
 * 3. Dependency preloading (mid-session)
 * 4. Owner ID processing for metadata events
 * 5. Before resource load event dispatching
 * 6. Player resource loading execution
 * 7. Comprehensive error handling and fallback
 *
 * **Owner ID Management:**
 * Processes ID3 owner IDs including:
 * - Wildcard owners ('*')
 * - Standard ID3 names
 * - Linear ad tracking IDs for CLID3 compliance
 *
 * **Extracted from PlayerManager:**
 * Consolidates resource management logic for better maintainability,
 * focused testing, and clear separation of concerns.
 */
export class ResourceManager implements IResourceManager {
	#logger: ILogger;
	#logLoading: ILogger;
	#tag: ITag;
	#utils: Utilities;

	// Resource storage
	#resource: IResourceConfig;

	constructor( tag: ITag, utils: Utilities ) {
		this.#tag = tag;
		this.#utils = utils;
		this.#logger = LogService.create( {
			prefixes: [ 'SmartTag:', 'Tag:', tag?.name || 'Unknown', 'ResourceManager:' ]
		} );
		this.#logLoading = LogService.create( { noTimestamp: true } );
		this.#logger.log( 'ResourceManager created' );
	}

	/**
	 * @description Current resource configuration
	 */
	get currentResource(): IResourceConfig {
		return this.#resource;
	}

	/**
	 * @description Returns promise that resolves with resource config object
	 */
	get resourceConfig(): Promise<IResourceConfig> {
		return new Promise(
			( resolve, reject ) => {
				const playerAdapter = this.#tag?.controller?.playerManager?.playerAdapter;
				if ( playerAdapter?.resourceConfig ) {
					playerAdapter.resourceConfig
						.then( ( data ) => {
							const resourceConfig = data?.resource;
							this.#logger.log( 'get resourceConfig:', 'resolving base resourceConfig:', resourceConfig );
							resolve( resourceConfig );
						} )
						.catch( ( error ) => {
							this.#logger.warn( 'get resourceConfig:', 'failed to get base resourceConfig from playerAdapter:', error );
							reject( { msg: 'playerAdapter.resourceConfig failed', success: false, error: error } );
						} );
				} else {
					reject( { msg: 'playerAdapter.resourceConfig not available', success: false } );
				}
			}
		);
	}

	/**
	 * @description Sets the current resource configuration
	 */
	setResource( resource: IResourceConfig ): void {
		this.#resource = resource;
		this.#logger.log( 'setResource:', resource );
	}

	/**
	 * @description Adds owner ID3 tags used so avia sends owned metadata events
	 *
	 * **Owner ID Processing:**
	 * Ensures proper ID3 metadata event handling by adding required owner IDs:
	 * - Wildcard owner ('*') for general metadata
	 * - Standard ID3 name for standard metadata
	 * - Linear ad tracking ID for CLID3 compliance (when applicable)
	 */
	addOwnerIDs( resource: IResourceConfig ): IResourceConfig {
		// ensure playback bucket exists
		if ( this.#utils.Type.isEmpty( resource?.playback ) ) {
			resource.playback = {};
		}
		const playback = resource.playback;

		// ensure id3OwnerIds is an array
		if ( this.#utils.Type.isArray( playback?.id3OwnerIds ) === false ) {
			playback.id3OwnerIds = [];
		}

		const isLinearAdTrackable = this.#tag?.model?.contentUtilities?.isLinearAdTrackable?.() === true;

		// IMPORTANT:
		// Keep wildcard '*' support for legacy/other ID3 use cases, but ensure '*' is LAST in the list.
		// Avia metadata cuepoints can surface with cue.id equal to the matched owner id from id3OwnerIds.
		// If '*' matches first, cue.id can become '*', which prevents CLID3 from matching com.paramount.vtg.
		// Putting specific owners before '*' allows CLID3 (and other specific owner listeners) to work.
		const ensureOwnerId = ( ownerId: string ): void => {
			if (
				this.#utils.Type.isString( ownerId )
				&&
				!this.#utils.Type.isEmpty( ownerId )
				&&
				playback.id3OwnerIds.includes( ownerId ) === false
			) {
				playback.id3OwnerIds.push( ownerId );
			}
		};

		// Standard SmartTag-owned ID3 domain
		ensureOwnerId( ID3.NAME );

		// add linear ad tracking id3 owner id per Avia CLID3 docs: https://avia.vtg.paramount.tech/avia-js/2.54.0/docs/guides/ad_clid3
		// - plugin option id3OwnerId MUST be present in resource.playback.id3OwnerIds
		if ( isLinearAdTrackable ) {
			const id3OwnerId = ID3.LINEAR_AD_TRACKING;
			const didAddLinearOwner = playback.id3OwnerIds.includes( id3OwnerId ) === false;
			ensureOwnerId( id3OwnerId );
			if ( didAddLinearOwner ) {
				this.#logger.log( 'addOwnerIDs:', 'added CLID3 linear ad tracking owner id:', id3OwnerId, 'id3OwnerIds:', playback.id3OwnerIds );
			}
		}

		// Wildcard owner for general metadata (keep, but move to end).
		ensureOwnerId( '*' );
		if ( playback.id3OwnerIds.includes( '*' ) ) {
			playback.id3OwnerIds = playback.id3OwnerIds.filter( ( id ) => id !== '*' );
			playback.id3OwnerIds.push( '*' );
		}

		return resource;
	}

	/**
	 * @description Loads Tag's default resource configuration into the player
	 *
	 * **Clear Intent Use Cases:**
	 * - PlayerEventOrchestrator: Auto-load after player creation
	 * - Cast error recovery: Reload same content after cast failure
	 * - Standard playback: Use Tag's built-in resource configuration
	 */
	async loadDefaultResource(): Promise<IPromiseResolveGeneric> {
		this.#logger.log( 'loadDefaultResource: Loading Tag default resource configuration' );

		let defaultResource: IResourceConfig;

		try {
			defaultResource = await this.resourceConfig;
		} catch ( error ) {
			this.#logger.warn( 'loadDefaultResource: resource config creation failed, dispatching ON_FATAL_ERROR', error );

			this.#tag.dispatchTagEvent(
				this.#tag.getTagEventVO(
					TagEvent.ON_FATAL_ERROR,
					this.#tag,
					{
						fatalError: {
							code: TagErrorCode.RCO_UNDEFINED,
							msg: {
								original: error?.msg || error?.result?.message,
								custom: this.#tag.errorManager.createErrorMessageByCode( TagErrorCode.RCO_UNDEFINED )
							},
							error: error
						}
					}
				)
			);

			return Promise.reject( { msg: 'Resource config creation failed', error } );
		}

		// Execute pipeline with default resource
		return this.#executeResourceLoadPipeline( defaultResource, 'default' );
	}

	/**
	 * @description Loads custom resource configuration into the player
	 *
	 * **Clear Intent Use Cases:**
	 * - Cast end resumption: Modified config with startTime for position resume
	 * - External custom load: Dynamic content switching from external consumers
	 * - Manual resource override: Testing or development scenarios
	 */
	async loadCustomResource( customConfig: IResourceConfig ): Promise<IPromiseResolveGeneric> {
		this.#logger.log( 'loadCustomResource: Loading provided custom resource configuration', customConfig );

		// Execute pipeline with custom resource
		return this.#executeResourceLoadPipeline( customConfig, 'custom' );
	}

	/**
	 * @description Returns whether resource manager has a valid resource
	 */
	hasResource(): boolean {
		return !this.#utils.Type.isEmpty( this.#resource );
	}

	/**
	 * @description Resets resource manager to initial state
	 */
	reset(): void {
		this.#resource = undefined;
		this.#logger.log( 'reset: Resource manager reset to initial state' );
	}

	/**
	 * @description Common resource loading pipeline implementation
	 *
	 * **Extracted Pipeline Logic:**
	 * All resource loading scenarios use this same pipeline with different resource sources.
	 * This eliminates code duplication and makes the flow crystal clear.
	 */
	#executeResourceLoadPipeline( resourceConfig: IResourceConfig, source: 'default' | 'custom' ): Promise<IPromiseResolveGeneric> {
		// 📢 INFORMATIONAL EVENT: Resource loading started
		this.#tag.dispatchTagEvent(
			this.#tag.getTagEventVO(
				TagEvent.ON_RESOURCE_LOAD_STARTED,
				this.#tag,
				{ resourceConfig }
			)
		);

		return new Promise<IPromiseResolveGeneric>( ( resolve, reject ) => {
			try {
				// Validate player readiness
				const player = this.#tag?.controller?.playerManager?.player;
				if ( !player || !player.isReady ) {
					const error = new Error( 'Player not ready for resource loading' );
					this.#dispatchLoadFailedEvent( error, resourceConfig );
					reject( { msg: 'Player not ready for resource loading', error } );
					return;
				}

				// Process resource config with owner IDs
				this.#resource = this.addOwnerIDs( resourceConfig );
				// Execute the loading pipeline
				this.#executeLoadSteps( resolve, reject, player );
			} catch ( error ) {
				this.#logger.warn( `#executeResourceLoadPipeline (${source}): Failed to start pipeline`, error );
				this.#dispatchLoadFailedEvent( error, resourceConfig );
				reject( { msg: 'Resource loading pipeline failed to start', error } );
			}
		} );
	}

	/**
	 * @description Executes the core resource loading steps
	 */
	#executeLoadSteps( resolve: ( value: IPromiseResolveGeneric ) => void, reject: ( reason: IPromiseResolveGeneric ) => void, player: IPlayerAdapterAPI ): void {
		// Validate resource exists after processing
		if ( !this.#utils.Type.isDefined( this.#resource ) ) {
			const resourceError = new Error( 'Resource configuration is undefined after processing' );
			this.#dispatchLoadFailedEvent( resourceError, this.#resource );
			reject( { msg: 'Resource configuration is undefined', error: resourceError } );
			return;
		}

		// Show loading animation on every load() start, on any platform where it's actually
		// constructed (desktop CHROMELESS + mobile web FULL/avia-js-ui) — CTV stays a no-op
		// via loadingAnimationManager being undefined there, not via a platform check here.
		if ( this.#utils.Type.isFunction( this.#tag?.view?.loadingAnimationManager?.show ) ) {
			this.#tag.view.loadingAnimationManager.show();
		}

		// Load dependencies and execute player loading
		this.#tag.controller.loadDependencies( false ).then(
			( data ) => {
				this.#logDetailedLoadingInfo( data );

				this.#logger?.log( '**Player resource loading prior to load:', this.#resource );

				// Load resource into player
				player.loadResource( this.#resource ).then(
					( resourceLoadResult ) => {
						this.#logger?.log( '**Player resource loading success:', resourceLoadResult );
						this.#dispatchLoadCompletedEvent();
						resolve( { msg: 'Resource loaded successfully', resource: this.#resource } );
					}
				).catch(
					( error ) => {
						this.#logger.warn( 'Player resource loading failure:', error );
						this.#dispatchLoadFailedEvent( error, this.#resource );
						reject( { msg: 'Player resource loading failed', error } );
					}
				);
			}
		).catch(
			( error ) => {
				this.#logger.warn( 'Dependencies loading failed. Stopping execution!', error );
				this.#handleDependencyLoadFailure( error, reject );
			}
		);
	}

	/**
	 * @description Helper method - dispatches resource load completed event
	 */
	#dispatchLoadCompletedEvent(): void {
		this.#tag.dispatchTagEvent(
			this.#tag.getTagEventVO(
				TagEvent.ON_RESOURCE_LOAD_COMPLETED,
				this.#tag,
				{ resourceConfig: this.#resource }
			)
		);
	}

	/**
	 * @description Helper method - dispatches resource load failed event
	 */
	#dispatchLoadFailedEvent( error: IImporterReject | Error, resourceConfig: IResourceConfig ): void {
		this.#tag.dispatchTagEvent(
			this.#tag.getTagEventVO(
				TagEvent.ON_RESOURCE_LOAD_FAILED,
				this.#tag,
				{ error, resourceConfig }
			)
		);
	}

	/**
	 * @description Helper method - logs detailed loading information
	 */
	#logDetailedLoadingInfo( data: IPromiseResolveGeneric ): void {
		this.#logLoading.log(
			'>>>>>>>>>>>>>>>',
			'\nSmartTag:',
			'\nTag:',
			this.#tag.uuid,
			this.#tag.name,
			'loadResource:',
			'\nparams:',
			this.#tag.params,
			'\nCONTENT.assetURL:',
			this.#tag.params.CONTENT.assetURL,
			'\napiMetadata.streamingUrl:',
			this.#tag?.model?.apiMetadata?.streamingUrl,
			'\npid:',
			this.#tag.params.CONTENT.pid,
			'\nresource:',
			this.#resource,
			'\nresource.location.mediaUrl:',
			this.#resource.location.mediaUrl,
			'\nad setup:',
			this.#tag?.model?.adUtilities?.adSetupInfo(),
			'\nimports:',
			data,
			'\n>>>>>>>>>>>>>>>'
		);
	}

	/**
	 * @description Helper method - handles dependency loading failures
	 */
	#handleDependencyLoadFailure( error: IImporterReject, reject: ( reason: IPromiseResolveGeneric ) => void ): void {
		// Dispatch fatal error (legacy compatibility)
		this.#tag.dispatchTagEvent(
			this.#tag.getTagEventVO(
				TagEvent.ON_FATAL_ERROR,
				this.#tag,
				{
					fatalError: {
						code: TagErrorCode.DEPENDENCY_LOAD_FAILURE,
						msg: {
							original: undefined,
							custom: this.#tag.errorManager.createErrorMessageByCode( TagErrorCode.DEPENDENCY_LOAD_FAILURE )
						},
						error: error
					}
				}
			)
		);

		this.#dispatchLoadFailedEvent( error, this.#resource );
		reject( { msg: 'Dependency loading failed', error } );
	}
}